import { VISITOR_FILTER_STYLE_OPTIONS } from '@typedream/components';
import { IAppProxyQuery, ICollectionColumn, ISpace, TEMPLATE_CATEGORY } from '@typedream/data';
import { Dispatch, SetStateAction } from 'react';
import type { Path } from 'slate';
import type { CollectionElement, DreamBlock, DreamValue } from '@typedream/core';
import { BaseDreamEditor } from '../../types';
import { ElementPluginOptions } from '../../plate';

export type ICollectionTemplate = CollectionElement['collection_template'];
export interface IItemPageTemplate {
  template: DreamValue;
  templateKey?: string;
  schema: { [key: string]: string };
  // schema key = nickname in the collection for that field.
  // schema value = the field name from data source
  schemaOrder: string[];
  previewImage: {
    image: string;
    imageDark: string;
  };
  custom: boolean; // if true, this template has been customized
}

export interface IItemPageTemplateList {
  key: string;
  template: IItemPageTemplate;
  category: TEMPLATE_CATEGORY;
  image: string;
  imageSidebar?: string;
  imageDark?: string;
  imageSidebarDark?: string;
}
export interface ICollectionTemplateList {
  key: string;
  template: ICollectionTemplate;
  image: string;
  imageSidebar?: string;
  imageDark?: string;
  imageSidebarDark?: string;
  defaultItemPageTemplate: IItemPageTemplateList;
}

export type CollectionPluginOptions = ElementPluginOptions & {
  collectionData: ICollectionData;
  sidebarTemplateOpen: boolean;
  setsidebarTemplateOpen: Dispatch<SetStateAction<boolean>>;
  templatePath: Path | null;
  settemplatePath: Dispatch<SetStateAction<Path | null>>;
  settingsOpen: boolean;
  setsettingsOpen: Dispatch<SetStateAction<boolean>>;
  setcollectionSettingsOpen?: Dispatch<SetStateAction<boolean>>;
  getVisitorFilterList: (collDataItem: ICollectionDataItem) => Promise<void>;
  visitorFilterList: string[];
  getIsTableConnected: (collDataItem: ICollectionDataItem) => boolean;
  showSchema: boolean;
  setcollectionSettingsPath?: Dispatch<SetStateAction<Path | null>>;
  getNextData?: (
    synchronize: boolean,
    collDataItem: ICollectionDataItem,
    cursorStates: ICollectionCursorState,
    setCursorState: (elementID: string, cursorStates: ICollectionCursorState) => void,
    editor: BaseDreamEditor,
    element: CollectionElement,
    synchronizeCollection: (pageCollData: ICollectionData, elementID?: string) => void,
    tableConnected: boolean
  ) => Promise<void>;

  getItemPageDataID: (collDataItem: ICollectionDataItem) => string;
  nextDataLoading: boolean;

  selectedTemplate: ICollectionTemplateList | null;
  hovered: ICollectionTemplateList | null;
  selectedCategory: TEMPLATE_CATEGORY | null;
  space: ISpace | null;
  loadingPage: boolean;
  initialValueEmpty?: DreamValue;
  collectionSynced?: boolean;
  setcollectionSynced?: Dispatch<SetStateAction<boolean>>;
  isContentEqualNoID?: (a: DreamValue, b: DreamValue) => boolean;
  unlimited?: boolean;
  limit?: number;
};

export type CollectionItemPluginOptions = ElementPluginOptions & {
  space: ISpace;
};
export type CollectionDataPluginOptions = ElementPluginOptions & {
  showSchema: boolean;
  columns?: {
    [key: string]: ICollectionColumn;
  };
  listColumns?: () => Promise<{
    [key: string]: ICollectionColumn;
  }>;
  listColumnsLoading?: boolean;
  updateSchema?: (newSchema: { [key: string]: string }) => void;
  schema?: {
    [key: string]: string;
  };
  schemaOrder?: string[];
  setopenConnectData?: React.Dispatch<React.SetStateAction<boolean>>;
};

export enum ICollectionIntegration {
  TYPEDREAM_CMS = 'td_cms',
  AIRTABLE = 'airtable',
  NOTION = 'notion',
  STRIPE = 'stripe',
  PAYPAL = 'paypal',
  OPENAI = 'openai',
}

export interface ICollectionCursorState {
  hasMore: boolean;
  fetchedCursor: string;
  nextCursor: string;
  data: { [field: string]: any }[];
  itemsPerPage: number;
  err: string;
  searchQuery?: string;
}

export enum ICollectionDataItemMetadataKeys {
  MAX_ITEMS = 'max_items',
  FILTER_COLUMN = 'filter_column',
  FILTER_COLUMN_TYPE = 'filter_column_type',
  FILTER_CONDITION = 'filter_condition',
  FILTER_VALUE = 'filter_value',
  SORT_COLUMN = 'sort_column',
  SORT_DIRECTION = 'sort_direction',
  SEARCH_COLUMNS = 'search_columns',
  SHOW_SEARCH_BAR = 'show_search_bar',
  VISITOR_FILTER_COLUMN = 'visitor_filter_column',
  VISITOR_FILTER_COLUMN_TYPE = 'visitor_filter_column_type',
  VISITOR_FILTER_STYLE = 'visitor_filter_style',
  VISITOR_FILTER_COLOR = 'visitor_filter_color',
}

export interface ICollectionDataItemMetadata {
  [ICollectionDataItemMetadataKeys.MAX_ITEMS]?: number;
  [ICollectionDataItemMetadataKeys.FILTER_COLUMN]?: string;
  [ICollectionDataItemMetadataKeys.FILTER_COLUMN_TYPE]?: string;
  [ICollectionDataItemMetadataKeys.FILTER_CONDITION]?: string;
  [ICollectionDataItemMetadataKeys.FILTER_VALUE]?: string | boolean;
  [ICollectionDataItemMetadataKeys.SORT_COLUMN]?: string;
  [ICollectionDataItemMetadataKeys.SORT_DIRECTION]?: string;
  [ICollectionDataItemMetadataKeys.SEARCH_COLUMNS]?: string[];
  [ICollectionDataItemMetadataKeys.SHOW_SEARCH_BAR]?: boolean;
  [ICollectionDataItemMetadataKeys.VISITOR_FILTER_COLUMN]?: string;
  [ICollectionDataItemMetadataKeys.VISITOR_FILTER_COLUMN_TYPE]?: string;
  [ICollectionDataItemMetadataKeys.VISITOR_FILTER_STYLE]?: VISITOR_FILTER_STYLE_OPTIONS | string;
  [ICollectionDataItemMetadataKeys.VISITOR_FILTER_COLOR]?: string;
}

export interface ICollectionDataItem {
  integration: ICollectionIntegration;
  [key: string]: any;
  metadata?: ICollectionDataItemMetadata;
  data?: { [field: string]: any }[];
  hasMore?: boolean;
  nextCursor?: string;
  fetchedCursor?: string;
  query?: IAppProxyQuery;
  queryResp?: any;
  tableID?: string;
}
export interface ICollectionPageDataItem extends ICollectionDataItem {
  childrenTemplatePageID: string;
}

export interface ICollectionData {
  [dataID: string]: ICollectionDataItem;
}
export interface ICollectionPageData {
  [dataID: string]: ICollectionPageDataItem;
}

export interface ISpaceRoute {
  page_id: string;
  children_template_page_id?: string;
  children_keys?: string[];
  children?: { [slug: string]: ISpaceRoute };
}
export interface ICollectionEditor extends BaseDreamEditor {
  setspaceCollectionData: (spaceCollData: ICollectionPageData) => void;
  setspaceRoutes: (routes: ISpaceRoute) => void;
  synchronizeCollection: (pageCollData: ICollectionData, elementID?: string) => void;
  spaceCollData: ICollectionPageData;
  routes: ISpaceRoute;
  collectionCursors: { [elementID: string]: ICollectionCursorState };
  setCursorState: (elementID: string, cursorState: ICollectionCursorState) => void;
  getCursorState: (elementID: string) => ICollectionCursorState;
}

export type IOptionsElement = UnionToIntersection<
  OmitKey<DreamBlock, 'type' | 'inputType' | 'logoType'>
> & {
  type: DreamBlock['type'];
  // there should be additional options property here
  // which is removed because we don't need to know
  // what the options are if we cast it into IOptionsElement
  // if you need the options property, please use
  // the property from @typedream/core
};

export type UnionToIntersection<T> = (T extends any ? (x: T) => any : never) extends (
  x: infer R
) => any
  ? R
  : never;

// This is useful for keeping the union type
// Simply using Omit will strip non-common properties from union
export type OmitKey<T, K extends string> = T extends object ? Omit<T, K> : never;
